import { jsPDF } from "jspdf";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { CircularProgress, Grid, Icon } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import { gql, useLazyQuery } from "@apollo/client";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import boxShadows from "assets/theme/base/boxShadows";
import EditableText from "components/EditableText";
import PdfHeader from "../components/pdfHeader";
import { formatMoneyPunto } from "utils/formatters";

const textosIniciales = [
  " ", // Número
  " ", // Solicitud N°
  " ", // Expediente
  "--/--/--", // Fecha Evaluación
];

const tipoResponsables = [
  { id: 1, label: "IVA Responsable Inscripto" },
  { id: 2, label: "IVA Responsable no Inscripto" },
  { id: 3, label: "IVA no Responsable" },
  { id: 4, label: "IVA Sujeto Exento" },
  { id: 5, label: "Consumidor Final" },
  { id: 6, label: "Responsable Monotributo" },
  { id: 7, label: "Sujeto no Categorizado" },
  { id: 8, label: "Proveedor del Exterior" },
  { id: 9, label: "Cliente del Exterior" },
  { id: 10, label: "IVA Liberado – Ley Nº 19.640" },
  { id: 11, label: "IVA Responsable Inscripto – Agente de Percepción" },
  { id: 12, label: "Pequeño Contribuyente Eventual" },
  { id: 13, label: "Monotributista Social" },
  { id: 14, label: "Pequeño Contribuyente Eventual Social" },
];

const tipoDocumentos = [
  { id: 80, label: "CUIT" },
  { id: 86, label: "CUIL" },
  { id: 96, label: "DNI" },
];

const PreAdjudicacion = () => {
  const documentsRef = useRef(null);
  const { id } = useParams();
  const { lg } = boxShadows;
  const [textArray, setTextArray] = useState(textosIniciales);
  const [editable, setEditable] = useState(true);
  const [data, setData] = useState(null);

  const [getPedido, { loading }] = useLazyQuery(
    gql`
      query getPedido($id: ID) {
        pedidosCompra(filter: { id: $id }) {
          id
          numeroExpediente
          licitacionFin
          detalles{
            id
            numero
            requierePack
            unidadPack
            materiaPrima{
              id
              nombre
              nombreCompra
              tipoUnidad {
                nombre
              }
            }
            cantidadVerificada
            observacion
            precioEstimado
            nombreServicio
            cotizaciones{
              id
              nombreAlternativo
              observacion
              precio
              materiaPrimaAlternativo
              cotizacion{
                id
                proveedor{
                  razonSocial
                  nombre
                  apellido
                }
              }
            }
            cotizacionSeleccionada {
              id
              nombreAlternativo
              observacion
              precio
              plazoEntrega
              materiaPrimaAlternativo
              cotizacion{
                id
                proveedor{
                  razonSocial
                  nombre
                  apellido
                }
              }
            }
            cotizacionSeleccionadaDesc
          }
          cotizaciones{
            id
            proveedor{
              tipoProveedor
              razonSocial
              nombre
              apellido
              tipoDocumento
              tipoResponsable
              documento
              localidad{
                nombre
                provincia{
                  nombre
                }
              }
            }
          }
        }
      }
    `,
    {
      onCompleted: (data) => {
        if (!data?.pedidosCompra[0]) return;
        setData(data.pedidosCompra[0]);
      },
    }
  );

  useEffect(() => {
    if (id) {
      getPedido({ variables: { id } });
    }
  }, [id]);

  useEffect(() => {
    if (data) {
      const newTextArray = [...textArray];
      newTextArray[0] = data.id;
      newTextArray[1] = data.id;
      setTextArray(newTextArray);
    }
  }, [data]);

  const handleGeneratePdf = async () => {
    setEditable(false);
    const document = documentsRef.current;
    const doc = new jsPDF({
      format: "a4",
      unit: "px",
      orientation: "landscape",
      hotfixes: ["px_scaling"],
      compress: true,
    });

    const addFooter = (doc, currentPage, totalPages) => {
      doc.setFontSize(8);
      doc.text(`Página ${currentPage} de ${totalPages}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, {
        align: "center",
      });
    };

    await doc.html(document, {
      margin: [40, 0, 40, 0],
      autoPaging: "text",
      callback: async function (doc) {
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          addFooter(doc, i, totalPages);
        }
        doc.save(`exd-pedidoNro-${data.id}-preAdjudicacionPorItem.pdf`);
      },
    });
    setEditable(true);
  };

  const handleTextChange = (e, index) => {
    const newTextArray = [...textArray];
    newTextArray[index] = e.target.innerText;
    setTextArray(newTextArray);
  };

  const licitacionFinalizada = data?.licitacionFin && new Date(data.licitacionFin).getTime() <= Date.now();

  if (!data) return (
    <EmpleadoLayout>
      <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={3}>
        {loading ? (<>
          <CircularProgress size={40} color="primary" />
          <SoftTypography variant="h5">Cargando</SoftTypography>
        </>) : (<>
          <Icon fontSize="large">search_off</Icon>
          <SoftTypography variant="h5">No se encontró el pedido</SoftTypography>
        </>)}
      </SoftBox>
    </EmpleadoLayout>
  );

  if (!licitacionFinalizada) {
    return (
      <EmpleadoLayout>
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={3}>
          <Icon fontSize="large" color="warning">lock</Icon>
          <SoftTypography variant="h5" color="warning">
            Documento disponible cuando finalice el período de presentación de ofertas.
          </SoftTypography>
        </SoftBox>
      </EmpleadoLayout>
    );
  }

  // Separar detalles en seleccionados y no seleccionados
  const detallesSeleccionados = data.detalles.filter(detalle =>
    detalle.cotizacionSeleccionada
  );

  const detallesNoSeleccionados = data.detalles.filter(detalle =>
    !detalle.cotizacionSeleccionada
  );

  return (
    <EmpleadoLayout>
      <SoftBox
        p={2}
        display="flex"
        alignItems="center"
        flexDirection="column"
        minHeight="100vh"
        height="100%"
      >
        <div ref={documentsRef}>
          <SoftBox
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "1123px",
              backgroundColor: "#ffffff",
              boxShadow: lg,
              px: 6,
            }}
            pb={5}
          >
            <PdfHeader />
            <SoftBox display="flex" flexDirection="column" gap={2} lineHeight={1.5} fontSize="12px">
              <Grid
                container
                border="1pt solid black"
                sx={{ '& > *:not(:last-child)': { borderBottom: "1pt solid black" } }}
              >
                <Grid item xs={12} fontWeight="bold" px={35} py={1} textAlign="center">
                  INFORME DE EVALUACIÓN DE PROCESO DE CONTRATACIÓN N°:{" "}
                  <EditableText
                    tooltip="Número"
                    editable={editable}
                    content={textArray[0]}
                    handleOnBlur={(e) => handleTextChange(e, 0)}
                  />
                  {" "}CONCURSO DE PRECIOS OTROS ORGANISMOS
                </Grid>
                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}
                >
                  <Grid item xs={2} px={1}>Método de Adjudicación </Grid>
                  <Grid item xs={10} px={1}>POR ITEM INDIVIDUAL AL MENOR PRECIO OFERTADO</Grid>
                </Grid>
                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}
                >
                  <Grid item xs={2} px={1}>Solicitud N° </Grid>
                  <Grid item xs={2} px={1}>
                    <EditableText
                      tooltip="Solicitud N°"
                      editable={editable}
                      content={textArray[1]}
                      handleOnBlur={(e) => handleTextChange(e, 1)}
                    />
                  </Grid>
                  <Grid item xs={2} px={1}>Fecha Evaluación</Grid>
                  <Grid item xs={2} px={1}>
                    <EditableText
                      tooltip="Fecha Evaluación"
                      editable={editable}
                      content={textArray[3]}
                      handleOnBlur={(e) => handleTextChange(e, 3)}
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}>
                  <Grid item xs={2} px={1}>Unidad Solicitante </Grid>
                  <Grid item xs={10} px={1}>{"FUNDACIÓN UPrO"}</Grid>
                </Grid>
              </Grid>
              <SoftTypography variant="h6" fontWeight="bold" textAlign="center" sx={{ width: '100%', borderBottom: '1px solid black' }}>
                Informe de PreAdjudicación
              </SoftTypography>
              {/* Tabla de Items Seleccionados */}
              <SoftTypography variant="h6">Items Seleccionados</SoftTypography>
              <SoftBox border="1pt solid black">
                <Grid
                  container
                  backgroundColor="#f0f0f0"
                  textAlign="center"
                  fontWeight="bold"
                >
                  <Grid item xs={1} px={1}>Item</Grid>
                  <Grid item xs={1} px={1}>Rnk</Grid>
                  <Grid item xs={1} px={1}>Cantidad</Grid>
                  <Grid item xs={1} px={1}>Unidad</Grid>
                  <Grid item xs={2} px={1}>Precio Unitario</Grid>
                  <Grid item xs={2} px={1}>Monto Total</Grid>
                  <Grid item xs={1} px={1}>Plazo Entrega</Grid>
                  <Grid item xs={2} px={1}>Observaciones/Justificación</Grid>
                </Grid>
                {detallesSeleccionados.map((detalle, index) => {
                  // Calcular el Rnk: Ordenar cotizaciones por precio y encontrar la posición de la seleccionada
                  let rnkCalculado = '-';
                  if (detalle.cotizacionSeleccionada && detalle.cotizaciones) {
                    // Ordenar las cotizaciones por precio de menor a mayor
                    const cotizacionesOrdenadas = [...detalle.cotizaciones].sort((a, b) => a.precio - b.precio);


                    // Buscar la posición de la cotización seleccionada por su ID directo
                    const idCotizacionSeleccionada = detalle.cotizacionSeleccionada.id;
                    if (idCotizacionSeleccionada) {
                      const indexEnOrdenadas = cotizacionesOrdenadas.findIndex(c =>
                        c.id === idCotizacionSeleccionada
                      );
                      if (indexEnOrdenadas !== -1) {
                        rnkCalculado = indexEnOrdenadas + 1; // Rnk comienza en 1
                      }
                    }
                  }

                  // Calcular monto total
                  const precioUnitario = detalle.cotizacionSeleccionada?.precio || 0;
                  const cantidad = detalle.cantidadVerificada || 0;
                  const montoTotal = precioUnitario * cantidad;

                  return (
                    <Grid container key={index} borderBottom="1pt solid #ccc">
                      <Grid item xs={1} px={1} textAlign="center">{detalle.numero}</Grid>
                      <Grid item xs={1} px={1} textAlign="center">{rnkCalculado}</Grid>
                      <Grid item xs={1} px={1} textAlign="center">{cantidad}</Grid>
                      <Grid item xs={1} px={1} textAlign="center">
                        {detalle?.requierePack === true && detalle?.unidadPack ? detalle?.unidadPack : detalle.materiaPrima?.tipoUnidad?.nombre || "SIN ESPECIFICAR"}
                      </Grid>
                      <Grid item xs={2} px={1} textAlign="center">
                        ${formatMoneyPunto(precioUnitario)}
                      </Grid>
                      <Grid item xs={2} px={1} textAlign="center">
                        ${formatMoneyPunto(montoTotal)}
                      </Grid>
                      <Grid item xs={1} px={1} textAlign="center">
                        {detalle.cotizacionSeleccionada?.plazoEntrega || '-'}
                      </Grid>
                      <Grid item xs={2} px={1} textAlign="center">
                        {detalle.cotizacionSeleccionadaDesc ||
                          `PreAdjudicado a ${[
                            detalle.cotizacionSeleccionada?.cotizacion?.proveedor?.razonSocial,
                            detalle.cotizacionSeleccionada?.cotizacion?.proveedor?.nombre,
                            detalle.cotizacionSeleccionada?.cotizacion?.proveedor?.apellido
                          ].filter(Boolean).join(" ")} 
                         por ser la oferta seleccionada.`}
                      </Grid>
                    </Grid>
                  );
                })}

                {/* Fila de total general */}
                <Grid
                  container
                  backgroundColor="#e0e0e0"
                  fontWeight="bold"
                  borderTop="2pt solid black"
                >
                  <Grid item xs={8} px={1} textAlign="center">TOTAL GENERAL:</Grid>
                  <Grid item xs={2} px={1} textAlign="center">
                    ${formatMoneyPunto(
                      detallesSeleccionados.reduce((total, detalle) => {
                        const precio = detalle.cotizacionSeleccionada?.precio || 0;
                        const cantidad = detalle.cantidadVerificada || 0;
                        return total + (precio * cantidad);
                      }, 0)
                    )}
                  </Grid>
                  <Grid item xs={2} px={1}></Grid>
                </Grid>
              </SoftBox>

              {/* Tabla de Items Sin Seleccionar (Desiertos/Fracasados) */}
              <SoftTypography variant="h6">Items Sin Seleccionar</SoftTypography>
              <SoftBox border="1pt solid black">
                <Grid
                  container
                  backgroundColor="#f0f0f0"
                  textAlign="center"
                  fontWeight="bold"
                >
                  <Grid item xs={1} px={1}>Item</Grid>
                  <Grid item xs={1} px={1}>Rnk</Grid>
                  <Grid item xs={10} px={1}>Observaciones/Justificación</Grid>
                </Grid>
                {detallesNoSeleccionados.map((detalle, index) => (
                  <Grid container key={index} borderBottom="1pt solid #ccc">
                    <Grid item xs={1} px={1} textAlign="center">{detalle.numero}</Grid>
                    <Grid item xs={1} px={1} textAlign="center">-</Grid>
                    <Grid item xs={10} px={1}>
                      {detalle.cotizacionSeleccionadaDesc ||
                        (detalle.cotizaciones && detalle.cotizaciones.length === 0
                          ? "Desierto - No se recibieron ofertas"
                          : "Fracasado - No se seleccionó cotización")}
                    </Grid>
                  </Grid>
                ))}
              </SoftBox>

              <SoftBox>
                <SoftBox fontWeight="bold" borderBottom="1pt solid black">Padron de Proveedores</SoftBox>{" "}
                {data?.cotizaciones.map((c, index) => (
                  <SoftBox key={index}>
                    - <strong>{[c.proveedor.razonSocial, c.proveedor.nombre, c.proveedor.apellido].join(" ").toUpperCase()}</strong>
                    {" "}
                    {[
                      tipoDocumentos.find(t => t.id == c.proveedor.tipoDocumento)?.label || "Documento",
                      c.proveedor.documento,
                      c.proveedor.localidad?.nombre,
                      c.proveedor.localidad?.provincia?.nombre,
                      tipoResponsables.find(t => t.id == c.proveedor.tipoResponsable)?.label
                    ].filter(e => e).join(" ").toUpperCase()}
                  </SoftBox>
                ))}
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </div>
        <SoftBox
          hidden={!editable}
          variant="contained"
          bgColor="white"
          p={2}
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            zIndex: 1000,
            boxShadow:
              "inset 0rem 0rem 0.0625rem 0.0625rem rgb(255 255 255 / 90%), 0rem 1.25rem 1.6875rem 0rem rgb(0 0 0 / 5%)",
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: `saturate(200%) blur(${pxToRem(30)})`,
          }}
        >
          <Grid container spacing={2} justifyContent="flex-end" id="button">
            <Grid item>
              <SoftButton variant="gradient" color="error" onClick={handleGeneratePdf}>
                <Icon fontWeight="bold">picture_as_pdf</Icon>
                &nbsp;&nbsp;Descargar PDF
              </SoftButton>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </EmpleadoLayout>
  );
};

export default PreAdjudicacion;